import { useQueries, useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { useI18n } from "../../i18n/I18nProvider";
import { messageKeyForError } from "../../i18n/errors";
import type { Configuration } from "../../lib/api-types";
import { Button } from "../../components/ui/Button";
import { ConfirmDialog } from "../../components/ui/ConfirmDialog";
import { EmptyState } from "../../components/ui/EmptyState";
import { ErrorState } from "../../components/ui/ErrorState";
import { ErrorText } from "../../components/ui/feedback";
import { LabelGrid } from "../../components/ui/LabelGrid";
import { PageHeader } from "../../components/ui/PageHeader";
import { ExportStatus } from "../export/ExportButton";
import { useConfigExport } from "../export/useConfigExport";
import { RosterSkeleton } from "../roster/RosterSkeleton";
import { StatusFilter, type StatusFilterValue } from "../roster/StatusFilter";
import { ConfigRow } from "./ConfigRow";
import { NameModal } from "./NameModal";
import { useConfigMutations } from "./mutations";
import { changesQuery, configurationsQuery, slotsQuery } from "./queries";

export interface ConfigurationsPageProps {
  workspaceId: string;
}

type Dialog =
  | { kind: "create" }
  | { kind: "rename"; configuration: Configuration }
  | { kind: "delete"; configuration: Configuration }
  | null;

/** Configurations list: label-grid rows with lock state, download state, one action and a menu. */
export function ConfigurationsPage({ workspaceId }: ConfigurationsPageProps) {
  const { t } = useI18n();
  const [filter, setFilter] = useState<StatusFilterValue>("active");
  const [dialog, setDialog] = useState<Dialog>(null);
  const list = useQuery(configurationsQuery(workspaceId, filter === "deleted"));
  const configurations = list.data ?? [];
  const live = filter === "active";
  const changes = useQueries({
    queries: live ? configurations.map((c) => changesQuery(workspaceId, c.id)) : [],
  });
  const slotLists = useQueries({
    queries: live ? configurations.map((c) => slotsQuery(workspaceId, c.id)) : [],
  });
  const mutations = useConfigMutations(workspaceId);
  const exporter = useConfigExport(workspaceId);

  const close = () => setDialog(null);
  const mutationError =
    mutations.create.error ??
    mutations.update.error ??
    mutations.duplicate.error ??
    mutations.remove.error ??
    mutations.restore.error;

  const renderBody = () => {
    if (list.isPending) return <RosterSkeleton />;
    if (list.isError) {
      return (
        <ErrorState
          action={<Button onClick={() => void list.refetch()}>{t("roster.tryAgain")}</Button>}
        >
          {t(messageKeyForError(list.error))}
        </ErrorState>
      );
    }
    if (configurations.length === 0) {
      if (!live) return <EmptyState>{t("configs.emptyDeleted")}</EmptyState>;
      return (
        <EmptyState
          action={
            <Button variant="primary" onClick={() => setDialog({ kind: "create" })}>
              {t("configs.create")}
            </Button>
          }
        >
          {t("configs.empty")}
        </EmptyState>
      );
    }
    return (
      <LabelGrid>
        {configurations.map((configuration, index) => (
          <ConfigRow
            key={configuration.id}
            workspaceId={workspaceId}
            configuration={configuration}
            changes={changes[index]?.data}
            playerCount={live ? slotLists[index]?.data?.length : undefined}
            busy={mutations.update.isPending}
            exporting={exporter.busy}
            onToggleLock={() =>
              mutations.update.mutate({
                configurationId: configuration.id,
                patch: { locked: !configuration.locked },
              })
            }
            onRename={() => setDialog({ kind: "rename", configuration })}
            onDuplicate={() => mutations.duplicate.mutate(configuration.id)}
            onDownload={() => void exporter.start(configuration.id, configuration.name)}
            onDelete={() => setDialog({ kind: "delete", configuration })}
            onRestore={() => mutations.restore.mutate(configuration.id)}
          />
        ))}
      </LabelGrid>
    );
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title={t("configs.title")}
        actions={
          <>
            <StatusFilter value={filter} onChange={setFilter} />
            {live ? (
              <Button variant="primary" onClick={() => setDialog({ kind: "create" })}>
                {t("configs.create")}
              </Button>
            ) : null}
          </>
        }
      />
      <ErrorText error={mutationError} />
      <ExportStatus exporter={exporter} />
      {renderBody()}

      {dialog?.kind === "create" ? (
        <NameModal
          title={t("configs.create")}
          initial=""
          submitLabel={t("configs.createSubmit")}
          busy={mutations.create.isPending}
          error={mutations.create.error}
          onClose={close}
          onSubmit={(name) => mutations.create.mutate(name, { onSuccess: close })}
        />
      ) : null}
      {dialog?.kind === "rename" ? (
        <NameModal
          title={t("configs.rename")}
          initial={dialog.configuration.name}
          submitLabel={t("common.save")}
          busy={mutations.update.isPending}
          error={mutations.update.error}
          onClose={close}
          onSubmit={(next) =>
            mutations.update.mutate(
              { configurationId: dialog.configuration.id, patch: { name: next } },
              { onSuccess: close },
            )
          }
        />
      ) : null}
      {dialog?.kind === "delete" ? (
        <ConfirmDialog
          title={t("common.delete")}
          message={t("configs.confirmDelete", { name: dialog.configuration.name })}
          confirmLabel={t("common.delete")}
          variant="danger"
          busy={mutations.remove.isPending}
          error={mutations.remove.error}
          onCancel={close}
          onConfirm={() => mutations.remove.mutate(dialog.configuration.id, { onSuccess: close })}
        />
      ) : null}
    </div>
  );
}
